/**
 * Maps report "advanced topics" cards to canonical course modules + lesson slugs.
 * Source of truth for module numbers/titles: data/numerologyCourse.js
 */

import { NUMEROLOGY_COURSE } from '../../data/numerologyCourse';

function findLessonBySlug(slug) {
  for (const mod of NUMEROLOGY_COURSE) {
    const lesson = mod.lessons.find((l) => l.slug === slug);
    if (lesson && !lesson.type) {
      return { module: mod, lesson };
    }
  }
  return null;
}

/** Topic id → lesson slug (verified against numerologyContent / course). */
export const ADVANCED_TOPIC_LESSON_SLUGS = {
  attitude: 'm3-attitude-number',
  balance: 'm3-balance-number',
  'hidden-passion': 'm3-hidden-passion',
  'personal-day': 'm4-personal-day',
  'life-cycles': 'm4-life-cycles',
  pinnacles: 'm4-pinnacles',
  challenges: 'm4-challenges',
  'karmic-lessons': 'm3-karmic-lessons',
  'karmic-debt': 'm3-karmic-debt',
};

const TOPIC_COPY = {
  attitude: {
    title: 'Attitude number',
    description:
      'How you tend to meet stress and first impressions early in life — a stance from birth month and day before the full chart complicates the picture.',
  },
  balance: {
    title: 'Balance number',
    description:
      'Derived from the initials of your birth name — the move you reach for when you need to steady yourself under pressure.',
  },
  'hidden-passion': {
    title: 'Hidden Passion',
    description:
      'Which digit appears most often in the letters of your birth name — a theme that can feel native even when core positions do not spotlight it.',
  },
  'personal-day': {
    title: 'Personal Day',
    description:
      'A light daily layer on top of Personal Month — scheduling nuance and small pacing choices, not a rewrite of identity.',
  },
  'life-cycles': {
    title: 'Life Cycles',
    description:
      'Three long chapters of life, each with a dominant digit — where you are in a multi-year arc, distinct from Personal Year weather.',
  },
  pinnacles: {
    title: 'Pinnacles',
    description:
      'Four peak themes tied to life quarters — opportunity and growth edges that pair with cycles and core numbers.',
  },
  challenges: {
    title: 'Challenges',
    description:
      'Friction between parts of the birth date — inner homework that pairs with pinnacles, not a character verdict.',
  },
  'karmic-lessons': {
    title: 'Karmic Lessons',
    description:
      'Digits 1–9 missing from the letters of the birth name — skills built through practice rather than absence of character.',
  },
  'karmic-debt': {
    title: 'Karmic Debt',
    description:
      'This position highlights lessons linked to specific repeating effort patterns. It points to areas where growth often comes through persistence, correction, and conscious awareness.',
  },
};

/**
 * Cards for the report advanced section: copy + resolved module/lesson + href.
 * @returns {Array<{ id: string, title: string, description: string, coverageLine: string, moduleLine: string, lessonSlug: string | null, lessonLabel: string | null, lessonHref: string | null, linkCta: string | null, linkStatus: 'exact' | 'missing' }>}
 */
export function buildAdvancedCourseParityTopics() {
  const order = [
    'attitude',
    'balance',
    'hidden-passion',
    'personal-day',
    'life-cycles',
    'pinnacles',
    'challenges',
    'karmic-lessons',
    'karmic-debt',
  ];

  return order.map((id) => {
    const copy = TOPIC_COPY[id];
    const slug = ADVANCED_TOPIC_LESSON_SLUGS[id];
    const found = slug ? findLessonBySlug(slug) : null;

    if (!found) {
      return {
        id,
        title: copy.title,
        description: copy.description,
        coverageLine:
          'Not included in this automated report. This topic is referenced in the course but does not yet have a dedicated lesson mapped here.',
        moduleLine: null,
        lessonSlug: null,
        lessonLabel: null,
        lessonHref: null,
        linkCta: null,
        linkStatus: 'missing',
      };
    }

    const { module, lesson } = found;
    const lessonHref = `/numerology/study/${lesson.slug}`;

    return {
      id,
      title: copy.title,
      description: copy.description,
      coverageLine:
        'Not included in this automated report. Calculation and how to read it are in the Astral Moon Magic course — use the lesson link below.',
      moduleLine: `Module ${module.number}: ${module.title}`,
      lessonSlug: lesson.slug,
      lessonLabel: lesson.label,
      lessonHref,
      linkCta: `Open lesson: ${lesson.label}`,
      linkStatus: 'exact',
    };
  });
}
